import React, { FC, memo, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { history, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
// 导入 useLocalState 使用的同一份 store 单例，直接操作缓存
import { store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import TextBubbles from '@home/components/TextBubbles';
import BasicImage from '@home/components/BasicImage';
import CommonBubble from '@home/components/CommonBubble';
import HighlightText from '@/pages/aigc/components/HighlightText';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import {
  ISkillData,
  ESkillSourceType,
  ESkillSecurityStatus,
} from '../../types';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import AgentModeAuthorDefaultIcon from '../../../svg/agent-mode-author-default-icon.svg';
import SkillInstalledIcon from '@/pages/aigc/assets/svg/skill-installed.svg';
import SkillHoverMoreIcon from '@/pages/aigc/assets/svg/skill-hover-more.svg';
import SkillTokenIcon from '@/pages/aigc/assets/svg/skill-token.svg';
import SkillInstallDownloadIcon from '@/pages/aigc/assets/svg/skill-install-download.svg';
import SkillCardTitleHoverArrow from '@/pages/aigc/assets/svg/skill-card-title-hover-arrow.svg';
import SkillInstallDropdown, {
  type ISkillInstallMenuItem,
} from '../SkillInstallDropdown';
import { ChatTypes } from '../../../ChatPage/types/chat';
import { PERSONAL_TOKEN_CENTER_URL } from '../../constants/skillDetail';
import { navigateToSkillDetail, navigateToHomeWithSkill } from '../../utils/navigation';
import {
  getSkillDisplayName,
  getSkillDescription,
  getSkillCategoryTagStyle,
  getSkillAuthorAvatarSrc,
} from '../../utils/display';
import './index.scss';
// ==================== 常量 & 类型 ====================

/** BEM 前缀 */
const PREFIX = 'dba-skill-card';

/** 下拉菜单项配置 */
export type IDropdownMenuItem = ISkillInstallMenuItem;

/** 操作图标配置项 */
interface IActionIconConfig {
  /** 唯一标识 */
  key: string;
  /** 图标资源路径 */
  icon: string;
  /** 无障碍替代文本 */
  alt: string;
  /** tooltip 国际化 key */
  tooltipId: string;
  /** 渲染类型：普通按钮 or 下拉菜单触发器 */
  type: 'button' | 'dropdown';
  /** 点击回调（button 类型使用） */
  onClick?: (e: React.MouseEvent) => void;
  /** 是否禁用（如 dialogable=0 时禁用立即对话） */
  disabled?: boolean;
}

/** 操作图标静态配置（不依赖 props，提到组件外避免每次渲染重建） */
const ACTION_ICONS_CONFIG: Omit<IActionIconConfig, 'onClick'>[] = [
  {
    key: 'chat',
    icon: SkillHoverMoreIcon,
    alt: 'chat',
    tooltipId: 'dba_skills_card_chat_tooltip',
    type: 'button',
  },
  {
    key: 'install',
    icon: SkillInstallDownloadIcon,
    alt: 'install',
    tooltipId: 'dba_skills_card_install_tooltip',
    type: 'dropdown',
  },
  {
    key: 'token',
    icon: SkillTokenIcon,
    alt: 'token',
    tooltipId: 'dba_skills_card_token_tooltip',
    type: 'button',
  },
];

// ==================== 组件 Props ====================

interface ISkillCardProps {
  /** Skill 数据 */
  skill: ISkillData;
  /** 搜索关键词（用于高亮） */
  searchKeyword?: string;
  /** 安装回调 */
  onInstall?: (skillId: string) => void;
  /** 下载回调 */
  onDownload?: (skill: ISkillData) => void;
  /** 可配置的下拉菜单项列表（覆盖默认） */
  dropdownMenuItems?: IDropdownMenuItem[];
  /** 当前 skill 是否正在下载中 */
  isDownloading?: boolean;
}

// ==================== 组件实现 ====================

/**
 * Skill 卡片组件
 */
const SkillCard: FC<ISkillCardProps> = ({
  skill,
  searchKeyword = '',
  onInstall,
  onDownload,
  dropdownMenuItems,
  isDownloading = false,
}) => {
  const { locale, formatMessage } = useIntl();
  const isEN = locale === LANGUAGE_EN;

  // 是否已安装
  const isInstalled = skill.is_installed;

  // 是否安全（只有 security_status === 1 才允许跳转和操作）
  const isSafe = skill.security_status === ESkillSecurityStatus.SAFE;

  // 来源文案（直接使用接口返回的标签）
  const sourceText = isEN ? skill.source_label_en : skill.source_label;

  // 分类文案（直接使用接口返回的分类字段）
  const categoryText = isEN ? (skill.category_en || skill.category) : skill.category;


  // 描述文案
  const description = useMemo(
    () => getSkillDescription(skill, isEN),
    [skill, isEN],
  );

  const displayName = useMemo(
    () => getSkillDisplayName(skill, isEN),
    [skill, isEN],
  );

  /** 分类标签颜色：由后端字段驱动，缺失时回退默认样式 */
  const categoryTagStyle = useMemo(
    () => getSkillCategoryTagStyle(skill),
    [skill],
  );

  const authorAvatarSrc = useMemo(
    () => getSkillAuthorAvatarSrc(skill),
    [skill],
  );

  /** 与头像一致：先 author_* / 接口字段，再 user_name，不用「DataBrain」等写死官方名 */
  const authorDisplayName = useMemo(
    () => skill.author_name?.trim() || '',
    [skill.author_name, skill.user_name],
  );

  /** 作者下方展示的创建时间（取日期部分：YYYY-MM-DD） */
  const createDateText = useMemo(() => {
    const raw = skill.update_time?.trim();
    if (!raw) return '—';
    return raw.replace('T', ' ').slice(0, 10);
  }, [skill.update_time]);

  /** 是否支持立即对话：1=支持，缺省按 1 */
  const isDialogable = Number(skill.dialogable ?? 1) === 1;

  /**
   * 使用 Skill：跳转首页并预填 tag（与输入框 @ 选中 skill 等价）
   * 仅已安装且安全的 Skill 写入 selectedSkill；其余情况仅打开首页
   */
  const handleUseSkill = useCallback(() => {
    if (!isInstalled || !isSafe) {
      store.setItem('__dba_copilot_tab__', ChatTypes.AI_CHAT);
      history.push({ pathname: '/agent/home', search: `?tab=${ChatTypes.AI_CHAT}` });
      return;
    }
    navigateToHomeWithSkill(skill, isEN);
  }, [isEN, isSafe, isInstalled, skill]);

  /** 点击标题（蓝字）→ Skill 详情页 */
  const handleOpenDetail = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '70090109',
      extInfo: JSON.stringify({ skill_id: skill.skill_id, skill_name: getSkillDisplayName(skill, isEN) }),
    });
    navigateToSkillDetail(skill.skill_id);
  }, [skill.skill_id, skill, isEN]);

  /** 点击卡片主体 → 下钻 Skill 详情页 */
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    buriedBtnPoint({
      pageId: '700501',
      buttonId: '70090108',
      extInfo: JSON.stringify({ skill_id: skill.skill_id, skill_name: getSkillDisplayName(skill, isEN) }),
    });
    navigateToSkillDetail(skill.skill_id);
  }, [skill.skill_id, skill, isEN]);

  // 下拉菜单项（缓存，避免每次渲染重建）
  const menuItems = useMemo<IDropdownMenuItem[] | undefined>(() => {
    if (dropdownMenuItems) {
      return dropdownMenuItems;
    }
    return undefined;
  }, [dropdownMenuItems]);

  // 操作图标列表（注入运行时 onClick 回调）
  const actionIcons = useMemo<IActionIconConfig[]>(() => {
    const clickHandlers: Record<string, (e: React.MouseEvent) => void> = {
      chat: (e) => {
        e.stopPropagation();
        if (!isDialogable) {
          return;
        }
        handleUseSkill();
      },
      token: (e) => {
        e.stopPropagation();
        window.open(PERSONAL_TOKEN_CENTER_URL, '_blank');
      },
    };
    return ACTION_ICONS_CONFIG.map((config) => {
      const nextConfig: IActionIconConfig = {
        ...config,
        onClick: clickHandlers[config.key],
      };
      if (config.key === 'chat') {
        nextConfig.disabled = !isDialogable;
      }
      return nextConfig;
    });
  }, [handleUseSkill, isDialogable]);

  // 渲染下拉菜单内容
  const renderActionIcon = useCallback((action: IActionIconConfig) => {
    if (action.type === 'dropdown') {
      return (
        <SkillInstallDropdown
          key={action.key}
          skill={skill}
          isEN={isEN}
          isDownloading={isDownloading}
          onInstall={onInstall}
          onDownload={onDownload}
          dropdownMenuItems={menuItems}
        />
      );
    }

    const iconBtn = (
      <div
        className={classNames(`${PREFIX}__icon-btn`, {
          [`${PREFIX}__icon-btn--disabled`]: action.disabled,
        })}
        onClick={action.onClick}
      >
        <BasicImage src={action.icon} alt={action.alt} className={`${PREFIX}__action-svg`} />
      </div>
    );

    return (
      <CommonBubble
        key={action.key}
        placement="top"
        content={formatMessage({ id: action.tooltipId })}
      >
        {iconBtn}
      </CommonBubble>
    );
  }, [formatMessage, isDownloading, isEN, menuItems, onDownload, onInstall, skill]);

  return (
    <div
      className={classNames(PREFIX, {
        [`${PREFIX}--installed`]: isInstalled,
      })}
      onClick={handleCardClick}
    >
      {/* 安装/未安装 角标 */}
      <div className={classNames(`${PREFIX}__status-badge`, {
        [`${PREFIX}__status-badge--installed`]: isInstalled,
        [`${PREFIX}__status-badge--not-installed`]: !isInstalled,
      })}>
        {isInstalled && <BasicImage src={SkillInstalledIcon} alt="" className={`${PREFIX}__status-icon`} />}
        <span>{isInstalled
          ? formatMessage({ id: 'dba_skills_card_installed' })
          : formatMessage({ id: 'dba_skills_card_not_installed' })
        }</span>
      </div>

      {/* 顶部区域: 图标 + 名称 + 标签（同行） */}
      <div className={`${PREFIX}__header`}>
        <div className={`${PREFIX}__category-icon`}>
          <BasicImage
            src={skill.category_icon || SkillsDefaultIcon}
            alt={displayName}
            errorSrc={SkillsDefaultIcon}
          />

        </div>
        <div className={`${PREFIX}__header-info`}>
          <div className={`${PREFIX}__name`} onClick={handleOpenDetail}>
            <span className={`${PREFIX}__name-text`}>
              <HighlightText
                name={displayName}
                keyword={searchKeyword}
                ellipsis
              />
            </span>
            <img
              src={SkillCardTitleHoverArrow}
              alt=""
              className={`${PREFIX}__name-arrow`}
              aria-hidden
            />
          </div>
          <div className={`${PREFIX}__tags`}>
            <span
              className={classNames(`${PREFIX}__tag`, {
                [`${PREFIX}__tag--official`]: skill.source_type === ESkillSourceType.OFFICIAL,
                [`${PREFIX}__tag--personal`]: skill.source_type === ESkillSourceType.PERSONAL,
              })}
            >
              {sourceText}
            </span>
            {categoryText && (
              <span
                className={classNames(
                  `${PREFIX}__tag`,
                  `${PREFIX}__tag--category`,
                )}
                style={categoryTagStyle}
              >
                {categoryText}
              </span>
            )}

          </div>
        </div>
      </div>

      {/* 描述 */}
      <div className={`${PREFIX}__content`}>
        <TextBubbles
          text={<HighlightText name={description} keyword={searchKeyword} />}
          rowNum={2}
          placement="top"
          className={`${PREFIX}__description`}
        />
      </div>

      {/* 底部：上传者信息 + 操作图标组 */}
      <div className={`${PREFIX}__footer`}>
        <div className={`${PREFIX}__author`}>
          <BasicImage
            src={authorAvatarSrc}
            alt={authorDisplayName || (isEN ? 'Skill author' : '作者')}
            className={`${PREFIX}__author-avatar`}
            errorSrc={AgentModeAuthorDefaultIcon}
          />
          <div className={`${PREFIX}__author-meta`}>
            <span className={`${PREFIX}__author-name`}>
              {authorDisplayName || '—'}
            </span>
            <span className={`${PREFIX}__create-time`}>
              {createDateText}
            </span>
          </div>
        </div>

        {/* 操作图标组 - map 循环渲染 */}
        <div className={`${PREFIX}__action-icons`}>
          {actionIcons.map(renderActionIcon)}
        </div>
      </div>
    </div>
  );
};

export default memo(SkillCard);
